/**
 * Which way a structure is being traded — ROADMAP 5.21.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE BUTTON SAYS WHAT THE TRADER WANTS, NOT WHAT TO DO TO THE LEGS
 *
 * The header fires a strategy with SELL or BUY. An earlier version mapped
 * those straight onto a transformation — SELL meant "invert the saved legs" —
 * and that was wrong for every template already stored in its short form:
 * pressing SELL on a Short Straddle inverted two SELL legs into two BUYs and
 * placed a long straddle under a red button labelled Sell.
 *
 * The mapping cannot be fixed by swapping it either, because the catalogue
 * holds BOTH forms: Short Straddle is stored sold and Long Straddle is stored
 * bought, so neither "SELL means as-is" nor "SELL means invert" is true of
 * all of them.
 *
 * So the intent and the transformation are separated. The intent is what the
 * trader pressed. Whether the legs get flipped is DERIVED by comparing that
 * intent with the side the structure is already written in. A button cannot
 * be wired up backwards, because no button names a transformation any more.
 *
 * DERIVED, NOT STORED. A trader may edit a strategy's legs (5.19), and a
 * column saying "this one is short" would go stale the moment they did. The
 * legs are the only thing that knows.
 * ─────────────────────────────────────────────────────────────────────────────
 */
/**
 * How far from the money a leg sits, for ranking only.
 *
 * A PREMIUM-SELECTED LEG IS TREATED AS THE FURTHEST. Its strike is not known
 * until the chain is read, and a leg chosen by "about ₹120 of premium" is a
 * wing in every use of it — never the body. Ranking it nearest would let an
 * unresolved wing decide the structure's side.
 */
function distanceOf(leg) {
    if (leg.strikeCriteria?.kind === 'ATM_OFFSET')
        return Math.abs(leg.strikeCriteria.steps);
    return Number.POSITIVE_INFINITY;
}
/**
 * Is this set of legs the short form of its structure, or the long one?
 *
 * DECIDED BY THE LEG NEAREST THE MONEY — the body. That is the leg carrying
 * the position: an Iron Condor's sold inner strikes make it a short
 * structure even though it buys as many contracts as it sells, and a Bull
 * Call Spread's bought ATM call makes it a long one even though it sells a
 * higher strike. Ties — a straddle's two legs, an Iron Fly's two sold bodies
 * — break towards the larger ratio and then towards the first leg, which is
 * only reached when the legs genuinely disagree at the same distance.
 *
 * NOT decided by net premium. That would need live prices to answer a
 * question the structure already answers, would be unanswerable with no
 * feed, and would flip a thin Batman from one label to the other on a tick.
 */
export function structureSideOf(legs) {
    let body = null;
    let bodyDistance = Number.POSITIVE_INFINITY;
    for (const leg of legs) {
        const distance = distanceOf(leg);
        if (body === null || distance < bodyDistance) {
            body = leg;
            bodyDistance = distance;
            continue;
        }
        if (distance === bodyDistance && leg.ratio > body.ratio)
            body = leg;
    }
    // No legs is not a short position. An empty structure has nothing to
    // invert, and SHORT would be a claim about a trade that does not exist.
    return body?.side === 'SELL' ? 'SHORT' : 'LONG';
}
/**
 * Do these legs need flipping to express the trader's intent?
 *
 * The whole rule: flip when the structure is not already written the way
 * they asked for it.
 */
export function shouldInvert(intent, legs) {
    return structureSideOf(legs) !== (intent === 'SELL' ? 'SHORT' : 'LONG');
}
/** The opposite side. */
export function oppositeSide(side) {
    return side === 'BUY' ? 'SELL' : 'BUY';
}
//# sourceMappingURL=strategyDirection.js.map